import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import VerifiedBadge, { isVerified } from "../components/VerifiedBadge";
import PremiumBadge from "../components/PremiumBadge";
import AffinityBadge from "../components/AffinityBadge";
import { MessageCircle, Sparkles, CalendarClock } from "lucide-react";

const PLACEHOLDER = "https://images.pexels.com/photos/8629727/pexels-photo-8629727.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Matches = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await client.get("/matches");
        setMatches(res.data.matches);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="matches-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        <span className="label-uppercase">Ligações</span>
        <h1 className="font-display text-3xl md:text-5xl mt-3 mb-10">Conexões mútuas</h1>

        {loading && <div className="text-[#A29F9B]">A carregar...</div>}

        {!loading && matches.length === 0 && (
          <div className="bg-[#161920] border border-white/5 rounded-2xl p-12 text-center" data-testid="matches-empty">
            <Sparkles className="w-8 h-8 text-[#C5A880] mx-auto mb-4" />
            <h3 className="font-display text-2xl mb-2">Ainda sem ligações mútuas</h3>
            <p className="text-[#A29F9B]">Continua a explorar o teu radar de afinidade.</p>
            <Link to="/discover" className="btn-primary inline-block mt-6" data-testid="matches-go-discover">Descobrir</Link>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {matches.map((m) => (
            <Link key={m.match_id} to={`/chat/${m.match_id}`}
              className="group bg-[#161920] border border-white/5 rounded-2xl overflow-hidden hover:border-[#C5A880]/40 transition-all"
              data-testid={`match-card-${m.match_id}`}>
              <div className="aspect-[3/4] overflow-hidden relative">
                <img src={m.profile.photos?.[0] || PLACEHOLDER} alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                {isVerified(m.profile) && (
                  <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
                    <VerifiedBadge size="sm" />
                    {m.profile.is_premium && <PremiumBadge />}
                  </div>
                )}
                {!isVerified(m.profile) && m.profile.is_premium && (
                  <div className="absolute top-3 right-3">
                    <PremiumBadge />
                  </div>
                )}
                {m.profile.agenda_url && (
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0C0E12]/80 backdrop-blur border border-[#C5A880]/40 text-[#C5A880] text-[10px] font-medium tracking-wide uppercase"
                    data-testid={`agenda-badge-${m.match_id}`}
                    title="Agenda disponível">
                    <CalendarClock className="w-3 h-3" /> Agenda
                  </div>
                )}
                {!m.profile.agenda_url && (
                  <div className="absolute top-3 left-3">
                    <AffinityBadge score={m.compatibility?.overall} size="sm" short />
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl">{m.profile.name}, {m.profile.age}</h3>
                  <span className="text-[#C5A880] font-display text-lg">{m.compatibility.overall}%</span>
                </div>
                <p className="text-sm text-[#A29F9B] mt-2 line-clamp-1">
                  {m.last_message ? m.last_message.text : "Diz olá pela primeira vez."}
                </p>
                <div className="mt-4 flex items-center justify-between gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 text-[#C5A880]">
                    <MessageCircle className="w-3 h-3" /> Abrir conversa
                  </span>
                  {m.profile.agenda_url && (
                    <button type="button"
                      onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); window.open(m.profile.agenda_url, "_blank", "noopener,noreferrer"); }}
                      className="inline-flex items-center gap-1 text-[#A29F9B] hover:text-[#C5A880] transition-colors"
                      data-testid={`agenda-quick-${m.match_id}`}>
                      <CalendarClock className="w-3 h-3" /> Agendar
                    </button>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Matches;
